import { takeLatest, put, call, select, delay } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { ROUTE_CODES } from 'JS/config/routes';
import { refreshCognitoIdToken } from 'JS/shared/providers/CognitoProvider';
import { showError } from 'JS/actionCreators/uiActions';
import config from 'JS/config/config';

import {
  VALIDATE_CREDENTIALS_REQUESTED,
  VALIDATE_TOKEN_REQUESTED,
  LOGOUT,
  validateCredentialsFailed,
  validateCredentialsSuccess,
  authSuccess,
  validateTokenFailure,
  verificationSuccess,
  verificationFailed,
  VERIFICATION_REQUESTED,
  sendResetPasswordEmailSuccess,
  sendResetPasswordEmailFailed,
  SEND_RESET_EMAIL_REQUESTED,
  resetPasswordSuccess,
  resetPasswordFailed,
  CONFIRM_PASSWORD_RESET_REQUESTED,
  validateTokenSuccess,
  refreshCognitoTokenSuccess,
  scheduleCognitoRefresh as scheduleCognitoRefreshAction,
  SCHEDULE_COGNITO_REFRESH,
  START_COGNITO_REFRESH,
  setAccountManagementRedirect
} from '../actionCreators/authActions';
import { reset } from '../actionCreators/generalActions';
import { authenticationProvider } from '../shared/providers/AuthenticationProvider';
import * as tokenHelper from '../shared/helpers/TokenHelper';
import { appServices } from '../shared/helpers/app-services/AppServices';
import { sendErrorToSentry } from '../logging';

import { getLastUnsuccessfulAction } from './selectors';
import redirectUserToReturnPath from './sagaHelpers/redirectUserToReturnPath';

const { MANAGE_ACCOUNT_URL } = config;

function* validateCredentials(action) {
  try {
    yield call(tokenHelper.removeAuthTokens);
    const { user, token, userTokens } = yield call(
      authenticationProvider.validateCredentials,
      action.email,
      action.password
    );
    yield put(validateCredentialsSuccess(user, token));
    yield call(tokenHelper.setSparkolToken, token, user.id);
    yield call(tokenHelper.setUserTokens, userTokens);
    yield put(authSuccess(user, token));

    yield call(externalRedirect);

    yield redirectUserToReturnPath();

    const lastAction = yield select(getLastUnsuccessfulAction);
    if (lastAction) {
      yield put(lastAction);
    }
  } catch (error) {
    sendErrorToSentry(error);
    yield put(validateCredentialsFailed(error));
  }
}

function* scheduleCognitoRefresh(action) {
  yield delay(action.scheduleTime);

  try {
    yield call(refreshCognitoToken);
  } catch (error) {
    yield put(showError({ message: 'We were unable to validate your credentials, please try logging in again.' }));
    yield call(logout);
  }
}

function* refreshCognitoToken(action) {
  const scheduleTime = yield call(tokenHelper.calculateScheduleDelayTime);

  if (scheduleTime > 0) {
    return yield put(scheduleCognitoRefreshAction(scheduleTime));
  }
  const result = yield call(refreshCognitoIdToken);
  if (result) {
    yield put(refreshCognitoTokenSuccess());
    yield call(tokenHelper.setUserTokens, result.AuthenticationResult);
  }
}

function* validateToken(action) {
  let cognitoResponse;
  let cognitoError;
  try {
    cognitoResponse = yield call(refreshCognitoIdToken);
    if (cognitoResponse) {
      yield put(refreshCognitoTokenSuccess());
      yield call(tokenHelper.setUserTokens, cognitoResponse.AuthenticationResult);
    }
  } catch (error) {
    sendErrorToSentry(error);
    cognitoError = error;
  }
  if (!action.token || !cognitoResponse) {
    yield put(validateTokenFailure(cognitoError ? cognitoError : 'no token'));
    yield call(tokenHelper.removeSparkolToken);
    return;
  }

  try {
    const result = yield call(authenticationProvider.validateToken);

    yield put(authSuccess(result.user, result.token));

    yield call(externalRedirect);
    yield put(validateTokenSuccess(result.user));
  } catch (error) {
    sendErrorToSentry(error);
    yield put(validateTokenFailure(error));
    yield call(tokenHelper.removeSparkolToken);
  }
}

function* externalRedirect(action) {
  const accountManagementRedirect = yield select(state => state.auth.accountManagementRedirect);

  if (accountManagementRedirect) {
    yield put(setAccountManagementRedirect(false));
    window.location.replace(`${MANAGE_ACCOUNT_URL}?referrer=loginredirect`);
    return yield;
  }
}

function* logout(action) {
  yield call(tokenHelper.removeAuthTokens);
  yield call(appServices.clearAssetCache);
  yield put(reset());
  yield put(push(ROUTE_CODES.LOGIN));
}

export function* recaptchaVerification(action) {
  try {
    yield call(appServices.verify, action.token);
    yield put(verificationSuccess());
  } catch (error) {
    sendErrorToSentry(error);
    yield put(verificationFailed(error));
  }
}

export function* sendResetEmail(action) {
  try {
    yield call(appServices.sendResetEmail, action.email, action.token);
    yield put(sendResetPasswordEmailSuccess());
  } catch (error) {
    sendErrorToSentry(error);
    yield put(sendResetPasswordEmailFailed(error));
  }
}

export function* resetPassword(action) {
  try {
    const { password, token, resetToken, verificationCode } = action;
    yield call(appServices.resetPassword, password, token, resetToken, verificationCode);
    yield put(resetPasswordSuccess());
  } catch (error) {
    sendErrorToSentry(error);
    yield put(resetPasswordFailed(error));
  }
}

function* authSagas() {
  yield takeLatest(VALIDATE_CREDENTIALS_REQUESTED, validateCredentials);
  yield takeLatest(VALIDATE_TOKEN_REQUESTED, validateToken);
  yield takeLatest(LOGOUT, logout);
  yield takeLatest(VERIFICATION_REQUESTED, recaptchaVerification);
  yield takeLatest(SEND_RESET_EMAIL_REQUESTED, sendResetEmail);
  yield takeLatest(CONFIRM_PASSWORD_RESET_REQUESTED, resetPassword);
  yield takeLatest(SCHEDULE_COGNITO_REFRESH, scheduleCognitoRefresh);
  yield takeLatest(START_COGNITO_REFRESH, refreshCognitoToken);
}

export default authSagas;
